import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Alert,
  FormHelperText,
  Divider,
  Menu,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useParams, useNavigate } from 'react-router-dom';
import { useIssueStore } from '../../store/issueStore';
import { useProjectStore } from '../../store/projectStore';
import { useLineStore } from '../../store/lineStore';
import { useAuthStore } from '../../store/authStore';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Issue, IssueCreate, IssueCreateForm, IssueUpdate, IssueType, IssueStatus, IssuePhoto } from '../../types/issue';
import { User, UserRole } from '../../types/models';
import { issueApi } from '../../api/issueApi';
import { userApi } from '../../api/userApi';
import { projectUserApi } from '../../api/projectUserApi';
import axios from '../../api/axios';
import { getImageUrl } from '../../utils/imageUrl';
import imageCompression from 'browser-image-compression';

interface ImportMetaEnv {
  readonly VITE_API_URL: string
}

interface ImportMeta {
  readonly env: ImportMetaEnv
}

export default function ProjectIssueForm() {
  const { id: projectId, issueId } = useParams<{ id: string; issueId?: string }>();
  const navigate = useNavigate();
  const { createIssue, updateIssue } = useIssueStore();
  const { currentProject, fetchProject } = useProjectStore();
  const { lines, fetchLines } = useLineStore();
  const { user } = useAuthStore();
  const [error, setError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  // Separate state for problem and counter measure photos
  const [selectedProblemFiles, setSelectedProblemFiles] = React.useState<File[]>([]);
  const [selectedCounterMeasureFiles, setSelectedCounterMeasureFiles] = React.useState<File[]>([]);
  const [users, setUsers] = React.useState<User[]>([]);
  const [projectUserIds, setProjectUserIds] = React.useState<number[]>([]);
  const [loading, setLoading] = React.useState(true);
  // Separate state for existing problem and counter measure photos
  const [existingProblemPhotos, setExistingProblemPhotos] = React.useState<IssuePhoto[]>([]);
  const [existingCounterMeasurePhotos, setExistingCounterMeasurePhotos] = React.useState<IssuePhoto[]>([]);
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  // Separate refs for problem and counter measure photo inputs
  const problemFileInputRef = React.useRef<HTMLInputElement>(null);
  const counterMeasureFileInputRef = React.useRef<HTMLInputElement>(null);
  const [cameraAnchorEl, setCameraAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentPhotoType, setCurrentPhotoType] = React.useState<'problem' | 'counter_measure'>('problem');
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [isCompressing, setIsCompressing] = React.useState(false);
  // In-app camera states
  const [cameraDialogOpen, setCameraDialogOpen] = React.useState(false);
  const [requestedFacingMode, setRequestedFacingMode] = React.useState<'user' | 'environment'>('environment');
  const [cameraStream, setCameraStream] = React.useState<MediaStream | null>(null);
  const [cameraLoading, setCameraLoading] = React.useState(false);
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  const [formData, setFormData] = React.useState<IssueCreateForm>({
    project_id: projectId ? parseInt(projectId, 10) : 0,
    line_id: 0,
    type: 'process',
    rank: 'a',
    part_number: '',
    part_name: '',
    problem: '',
    counter_measure: '',
    final_counter_measure: '',
    status: 'draft',
    pic_id: null,
    reviewer_pic_id: null,
    approver_pic_id: null,
    completion_date: ''
  });

  const [validationErrors, setValidationErrors] = useState<{
    pic_id?: string;
    reviewer_pic_id?: string;
    approver_pic_id?: string;
    completion_date?: string;
  }>({});

  // Filter users by role and project
  const cmPicUsers = React.useMemo(() => {
    return users.filter(user => {
      // Check if user is mapped to the project
      if (!projectUserIds.includes(user.id)) return false;
      
      if (!user.roles) return false;
      
      // Handle different possible formats of roles
      return user.roles.some(role => {
        if (typeof role === 'string') {
          return role === 'cm_pic';
        } else {
          return role === UserRole.CM_PIC;
        }
      });
    });
  }, [users, projectUserIds]);

  const approverUsers = React.useMemo(() => {
    return users.filter(user => {
      // Check if user is mapped to the project
      if (!projectUserIds.includes(user.id)) return false;
      
      if (!user.roles) return false;
      
      // Handle different possible formats of roles
      return user.roles.some(role => {
        if (typeof role === 'string') {
          return role === 'approver';
        } else {
          return role === UserRole.APPROVER;
        }
      });
    });
  }, [users, projectUserIds]);

  const reviewerUsers = React.useMemo(() => {
    return users.filter(user => {
      // Check if user is mapped to the project
      if (!projectUserIds.includes(user.id)) return false;
      
      if (!user.roles) return false;
      
      // Handle different possible formats of roles
      return user.roles.some(role => {
        if (typeof role === 'string') {
          return role === 'reviewer';
        } else {
          return role === UserRole.REVIEWER;
        }
      });
    });
  }, [users, projectUserIds]);

  const formatWord = (word: string) => {
    return word
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Fetch project and lines data
  React.useEffect(() => {
    const fetchData = async () => {
      if (projectId) {
        try {
          await fetchProject(parseInt(projectId, 10));
          await fetchLines({ project_id: parseInt(projectId, 10) });
          
          // Fetch project users
          const projectUsers = await projectUserApi.getProjectUsers(parseInt(projectId, 10));
          setProjectUserIds(projectUsers);
        } catch (err) {
          console.error('Failed to fetch project data:', err);
          setError('Failed to fetch project data');
        }
      }
    };
    fetchData();
  }, [projectId, fetchProject, fetchLines]);

  // Fetch issue data if editing
  React.useEffect(() => {
    const loadIssue = async () => {
      if (issueId) {
        try {
          const issue = await issueApi.getIssue(
            parseInt(issueId, 10),
            projectId ? parseInt(projectId, 10) : undefined
          );
          console.log('Loaded issue data:', issue);
          
          // Format the completion_date to YYYY-MM-DD for the datepicker
          let formattedCompletionDate = '';
          if (issue.completion_date) {
            // Parse the date and adjust for timezone to prevent date shifting
            const dateStr = issue.completion_date;
            // Use split and join to ensure consistent date format regardless of input
            const [year, month, day] = dateStr.split('T')[0].split('-');
            formattedCompletionDate = [year, month, day].join('-');
          }
          
          setFormData({
            ...issue,
            line_id: issue.line_id,
            pic_id: issue.pic_id,
            reviewer_pic_id: issue.reviewer_pic_id,
            approver_pic_id: issue.approver_pic_id,
            completion_date: formattedCompletionDate
          });
          console.log('Form data after setting:', {
            ...issue,
            line_id: issue.line_id,
            pic_id: issue.pic_id,
            reviewer_pic_id: issue.reviewer_pic_id,
            approver_pic_id: issue.approver_pic_id,
            completion_date: formattedCompletionDate
          });
          if (issue.photos) {
            // Categorize photos by type
            const problemPhotos = issue.photos.filter(photo => photo.photo_type === 'problem' || !photo.photo_type);
            const counterMeasurePhotos = issue.photos.filter(photo => photo.photo_type === 'counter_measure');
            
            setExistingProblemPhotos(problemPhotos);
            setExistingCounterMeasurePhotos(counterMeasurePhotos);
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load issue');
        }
      }
    };
    loadIssue();
  }, [issueId]);

  // Fetch users for PIC selection
  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const fetchedUsers = await userApi.getUsers();
        // Filter out inactive users
        const activeUsers = fetchedUsers.filter(user => user.is_active);
        setUsers(activeUsers);
        setError(null);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setError('Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const compressImage = async (file: File): Promise<File> => {
    try {
      setIsCompressing(true);
      
      // Image compression options
      const options = {
        maxSizeMB: 1,             // Maximum size in MB
        maxWidthOrHeight: 1920,   // Max width/height in pixels
        useWebWorker: true,       // Use web worker for better performance
        fileType: file.type       // Maintain the original file type
      };
      
      // Compress the image
      const compressedFile = await imageCompression(file, options);
      
      // Create a new file with the original name but compressed data
      const newFile = new File([compressedFile], file.name, {
        type: file.type,
        lastModified: file.lastModified
      });
      
      return newFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      // If compression fails, return the original file
      return file;
    } finally {
      setIsCompressing(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, photoType: 'problem' | 'counter_measure') => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      
      // Show compression message
      setSnackbarMessage('Compressing images...');
      setSnackbarOpen(true);
      
      // Compress each image
      const compressedFiles = await Promise.all(
        files.map(file => compressImage(file))
      );
      
      // Update state with compressed files based on photo type
      if (photoType === 'problem') {
        setSelectedProblemFiles((prev) => [...prev, ...compressedFiles]);
      } else {
        setSelectedCounterMeasureFiles((prev) => [...prev, ...compressedFiles]);
      }
      
      // Show success message
      setSnackbarMessage('Images compressed and ready to upload');
      setSnackbarOpen(true);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? null : 
        name === 'line_id' || name === 'pic_id' || name === 'approver_pic_id' || name === 'reviewer_pic_id' 
          ? parseInt(value, 10) 
          : value,
    }));
  };

  const handleRemoveFile = (index: number, photoType: 'problem' | 'counter_measure') => {
    if (photoType === 'problem') {
      setSelectedProblemFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setSelectedCounterMeasureFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent, status: IssueStatus = 'draft') => {
    // Use lowercase status as expected by the backend
    // For updates, if status is 'draft' (default), keep the current status
    const statusToUse = status === 'draft' && issueId ? formData.status : status;
    const normalizedStatus = statusToUse.toLowerCase() as any;
    console.log('Submitting with status:', normalizedStatus, 'type:', typeof normalizedStatus);
    e.preventDefault();
    
    // Validate mandatory fields before submission
    const errors: {
      pic_id?: string;
      approver_pic_id?: string;
      completion_date?: string;
    } = {};
    
    if (!formData.pic_id) {
      errors.pic_id = "C/M PIC is required";
    }
    
    if (!formData.approver_pic_id) {
      errors.approver_pic_id = "Approver is required";
    }
    
    if (!formData.completion_date) {
      errors.completion_date = "Target Completion Date is required";
    }
    
    // If there are validation errors, display them and stop submission
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Clear any previous validation errors
    setValidationErrors({});
    setUploading(true);
    setError(null);

    try {
      if (!projectId) {
        throw new Error('Project ID is missing');
      }

      // Create the base issue data
      const issueData: IssueCreate | IssueUpdate = {
        project_id: parseInt(projectId, 10),
        type: formData.type!,
        rank: formData.rank!,
        problem: formData.problem!,
        counter_measure: formData.counter_measure || '',
        final_counter_measure: formData.final_counter_measure || '',
        // Explicitly set status to ensure it's in the correct case
        status: normalizedStatus as any,
        pic_id: formData.pic_id,
        reviewer_pic_id: formData.reviewer_pic_id,
        approver_pic_id: formData.approver_pic_id,
        completion_date: formData.completion_date,
        line_id: formData.type === 'process' ? formData.line_id! : undefined
      };

      // Add line_id, part_number, and part_name only for 'process' type
      if (formData.type === 'process') {
        (issueData as IssueCreate).line_id = formData.line_id!;
        (issueData as IssueCreate).part_number = formData.part_number!;
        (issueData as IssueCreate).part_name = formData.part_name!;
      }

      if (issueId) {
        // Update existing issue
        // Create a copy without project_id for the update operation
        const updateData: IssueUpdate = { ...issueData };
        
        // Remove project_id from updateData as it's not needed for updates
        if ('project_id' in updateData) {
          delete updateData.project_id;
        }
        
        // Handle line_id based on issue type
        if (formData.type === 'process') {
          // For process type, ensure line_id is included
          updateData.line_id = formData.line_id!;
        } else {
          // For general type, set line_id to undefined
          updateData.line_id = undefined;
        }
        
        // Ensure the status is set correctly based on the button clicked
        updateData.status = normalizedStatus;
        
        await issueApi.updateIssue(
          parseInt(issueId, 10),
          updateData,
          projectId ? parseInt(projectId, 10) : undefined
        );

        // Upload new problem photos if any
        if (selectedProblemFiles.length > 0) {
          await issueApi.uploadPhotos(
            parseInt(issueId, 10),
            selectedProblemFiles,
            'problem',
            projectId ? parseInt(projectId, 10) : undefined
          );
        }
        
        // Upload new counter measure photos if any
        if (selectedCounterMeasureFiles.length > 0) {
          await issueApi.uploadPhotos(
            parseInt(issueId, 10),
            selectedCounterMeasureFiles,
            'counter_measure',
            projectId ? parseInt(projectId, 10) : undefined
          );
        }

        navigate(`/projects/${projectId}`);
      } else {
        // Create new issue
        const newIssue = await issueApi.createIssue(issueData as IssueCreate);

        // Upload problem photos if any
        if (selectedProblemFiles.length > 0 && newIssue.id) {
          await issueApi.uploadPhotos(
            newIssue.id,
            selectedProblemFiles,
            'problem',
            newIssue.project_id
          );
        }
        
        // Upload counter measure photos if any
        if (selectedCounterMeasureFiles.length > 0 && newIssue.id) {
          await issueApi.uploadPhotos(
            newIssue.id,
            selectedCounterMeasureFiles,
            'counter_measure',
            newIssue.project_id
          );
        }

        navigate(`/projects/${projectId}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/projects/${projectId}`);
  };

  const handleDeletePhoto = async (photoId: number) => {
    if (!issueId) return;

    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        await issueApi.deletePhoto(
          parseInt(issueId, 10),
          photoId,
          projectId ? parseInt(projectId, 10) : undefined
        );
        // Update the local state to remove the deleted photo
        // Check both photo collections and update the appropriate one
        setExistingProblemPhotos(prevPhotos => prevPhotos.filter(photo => photo.id !== photoId));
        setExistingCounterMeasurePhotos(prevPhotos => prevPhotos.filter(photo => photo.id !== photoId));
      } catch (error) {
        console.error('Failed to delete photo:', error);
        setError('Failed to delete photo');
      }
    }
  };

  const handleClosePreview = () => {
    setSelectedImage(null);
  };
  
  const getStatusPercentage = (status: string) => {
    switch (status) {
      case 'waiting_response': return 0.25;
      case 'waiting_review': return 0.5;
      case 'waiting_approval': return 0.75;
      case 'completed': return 1;
      default: return 0;
    }
  };

  const handleCameraMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, photoType: 'problem' | 'counter_measure' = 'problem') => {
    setCameraAnchorEl(event.currentTarget);
    setCurrentPhotoType(photoType);
  };

  const handleCameraMenuClose = () => {
    setCameraAnchorEl(null);
  };

  const startCamera = async (facingMode: 'user' | 'environment') => {
    try {
      setCameraLoading(true);
      setCameraError(null);
      // Prefer specific facing mode when possible
      const constraints: MediaStreamConstraints = {
        audio: false,
        video: {
          facingMode: facingMode === 'environment' ? { exact: 'environment' } : 'user'
        } as MediaTrackConstraints
      };

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        // Retry with a generic video constraint if exact facing mode isn't available
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }

      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (error) {
      console.error('Failed to start camera:', error);
      setCameraError('Unable to access the camera. Please check permissions.');
    } finally {
      setCameraLoading(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
    }
    setCameraStream(null);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleCameraDialogClose = () => {
    stopCamera();
    setCameraDialogOpen(false);
  };

  const handleTakeSnapshot = async () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current ?? document.createElement('canvas');
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);

    await new Promise<void>((resolve) => setTimeout(() => resolve(), 0));

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Show compression message
      setSnackbarMessage('Compressing image...');
      setSnackbarOpen(true);

      const compressed = await compressImage(file);

      if (currentPhotoType === 'problem') {
        setSelectedProblemFiles(prev => [...prev, compressed]);
      } else {
        setSelectedCounterMeasureFiles(prev => [...prev, compressed]);
      }

      setSnackbarMessage('Image captured and ready to upload');
      setSnackbarOpen(true);

      handleCameraDialogClose();
    }, 'image/jpeg', 0.92);
  };

  const handleCapturePhoto = async (cameraType: 'user' | 'environment', photoType: 'problem' | 'counter_measure' = 'problem') => {
    // Close the menu and open in-app camera dialog
    handleCameraMenuClose();
    setCurrentPhotoType(photoType);
    setRequestedFacingMode(cameraType);

    if (navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function') {
      setCameraDialogOpen(true);
      await startCamera(cameraType);
    } else {
      // Fallback for devices that don't support MediaDevices API
      setSnackbarMessage('Camera not supported. Please use the "Add Photos" button to upload existing photos.');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Stop camera when dialog is closed or component unmounts
  React.useEffect(() => {
    if (!cameraDialogOpen) {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [cameraDialogOpen]);

  if (!currentProject) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs
        items={[
          { label: 'Events', path: '/projects' },
          { label: currentProject.name, path: `/projects/${projectId}` },
          { label: issueId ? 'Edit BIRA' : 'New BIRA' },
        ]}
      />

      <Button
        variant="outlined"
        onClick={() => navigate(`/projects/${projectId}`)}
        sx={{ mt: 2, mb: 2 }}
      >
        Back to Event
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb:2 }}>
          {issueId ? 'Edit BIRA' : 'Create New BIRA'}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            {/* Left Column */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>

                <Divider textAlign="left" sx={{ mb: 2 }}>Problem Details</Divider>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Type *</InputLabel>
                      <Select
                        name="type"
                        value={formData.type || ''}
                        onChange={handleSelectChange}
                        label="Type"
                      >
                        <MenuItem value="">Select a type</MenuItem>
                        <MenuItem value="general">General</MenuItem>
                        <MenuItem value="process">Process</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <FormControl fullWidth>
                      <InputLabel>Rank *</InputLabel>
                      <Select
                        name="rank"
                        value={formData.rank || ''}
                        onChange={handleSelectChange}
                        label="Rank"
                      >
                        <MenuItem value="">Select a rank</MenuItem>
                        <MenuItem value="a">A</MenuItem>
                        <MenuItem value="b">B</MenuItem>
                        <MenuItem value="c">C</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Process-specific fields */}
                {formData.type === 'process' && (
                  <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={11}>
                        <FormControl fullWidth>
                          <InputLabel>Line *</InputLabel>
                          <Select
                            name="line_id"
                            value={formData.line_id?.toString() || ''}
                            onChange={handleSelectChange}
                            label="Line"
                            required={formData.type === 'process'}
                          >
                            <MenuItem value="">Select a line</MenuItem>
                            {lines.map((line) => (
                              <MenuItem key={line.id} value={line.id}>
                                {line.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {/* Part Number and Name */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={11}>
                        <TextField
                          name="part_number"
                          label="Part Number"
                          value={formData.part_number || ''}
                          onChange={handleTextChange}
                          fullWidth
                          required={formData.type === 'process'}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={11}>
                        <TextField
                          name="part_name"
                          label="Part Name"
                          value={formData.part_name || ''}
                          onChange={handleTextChange}
                          fullWidth
                          required={formData.type === 'process'}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={8}>
                    <FormControl fullWidth error={!!error}>
                      <TextField
                        name="issuer_id"
                        label="Issuer"
                        value={(formData as any).created_by_name || (issueId ? 'Loading issuer...' : user?.full_name || 'Current User')}
                        InputProps={{
                          readOnly: true,
                          sx: { bgcolor: '#f5f5f5' }
                        }}
                        fullWidth
                        disabled={loading}
                      />
                      {error && <FormHelperText>{error}</FormHelperText>}
                      {loading && <FormHelperText>Loading users...</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      name="created_on"
                      label="Issued Date"
                      value={
                        // If editing an existing issue, use the creation date
                        // Otherwise, show today's date for new issues
                        issueId && (formData as any).created_on 
                          ? (() => {
                              // Parse the ISO date string from created_on
                              const date = new Date((formData as any).created_on);
                              // Format as DD-MMM-YYYY
                              const day = date.getDate().toString().padStart(2, '0');
                              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                              const month = months[date.getMonth()];
                              const year = date.getFullYear();
                              return `${day}-${month}-${year}`;
                            })()
                          : (() => {
                              // Format today's date as DD-MMM-YYYY
                              const today = new Date();
                              const day = today.getDate().toString().padStart(2, '0');
                              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                              const month = months[today.getMonth()];
                              const year = today.getFullYear();
                              return `${day}-${month}-${year}`;
                            })()
                      }
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        sx: { bgcolor: '#f5f5f5' }
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={8}>
                    <FormControl fullWidth error={!!error}>
                      <TextField
                        name="status"
                        label="Status"
                        value={formatWord(formData.status) || ''}
                        InputProps={{
                          readOnly: true,
                          sx: { bgcolor: '#f5f5f5' }
                        }}
                        fullWidth
                        disabled={loading}
                      />
                      {error && <FormHelperText>{error}</FormHelperText>}
                      {loading && <FormHelperText>Loading users...</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={8}>
                    <FormControl fullWidth error={!!validationErrors.pic_id || !!error}>
                      <InputLabel>C/M PIC *</InputLabel>
                      <Select
                        name="pic_id"
                        value={formData.pic_id?.toString() || ''}
                        onChange={handleSelectChange}
                        label="C/M PIC"
                        disabled={loading}
                        required
                      >
                        <MenuItem value="">Select a PIC</MenuItem>
                        {cmPicUsers.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.full_name}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.pic_id && <FormHelperText error>{validationErrors.pic_id}</FormHelperText>}
                      {error && <FormHelperText>{error}</FormHelperText>}
                      {loading && <FormHelperText>Loading users...</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={8}>
                    <FormControl fullWidth error={!!validationErrors.reviewer_pic_id || !!error}>
                      <InputLabel>Reviewer *</InputLabel>
                      <Select
                        name="reviewer_pic_id"
                        value={formData.reviewer_pic_id?.toString() || ''}
                        onChange={handleSelectChange}
                        label="Reviewer"
                        disabled={loading}
                        required
                      >
                        <MenuItem value="">Select Reviewer</MenuItem>
                        {reviewerUsers.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.full_name}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && <FormHelperText>{error}</FormHelperText>}
                      {loading && <FormHelperText>Loading users...</FormHelperText>}
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={8}>
                    <FormControl fullWidth error={!!validationErrors.approver_pic_id || !!error}>
                      <InputLabel>Approver *</InputLabel>
                      <Select
                        name="approver_pic_id"
                        value={formData.approver_pic_id?.toString() || ''}
                        onChange={handleSelectChange}
                          label="Approver"
                          disabled={loading}
                          required
                        >
                          <MenuItem value="">Select Approver PIC</MenuItem>
                          {approverUsers.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                              {user.full_name}
                            </MenuItem>
                          ))}
                        </Select>
                        {validationErrors.approver_pic_id && <FormHelperText error>{validationErrors.approver_pic_id}</FormHelperText>}
                        {error && <FormHelperText>{error}</FormHelperText>}
                        {loading && <FormHelperText>Loading users...</FormHelperText>}
                      </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      name="completion_date"
                      label="Target Completion Date"
                      type="date"
                      value={formData.completion_date || ''}
                      onChange={handleTextChange}
                      fullWidth
                      required
                      error={!!validationErrors.completion_date}
                      helperText={validationErrors.completion_date}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        max: '2100-12-31' // Set a reasonable maximum date
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 2, mt: 2 , pl: 1}}>
                  <Divider textAlign="left">Progress</Divider>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ position: 'relative', width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {(() => {
                        const percentage = getStatusPercentage(formData.status);
                        let imageName = 'progress-25.png';
                        
                        if (percentage <= 0) {
                          // For 0% progress, just show the text without an image
                          return (
                            <Box 
                              sx={{ 
                                position: 'relative', 
                                width: '80px', 
                                height: '80px',
                                border: '1px solid #ccc',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '10px',
                                  fontWeight: 'bold'
                                }}
                              >
                                0%
                              </Typography>
                            </Box>
                          );
                        } else if (percentage <= 0.25) {
                          imageName = 'progress-25.png';
                        } else if (percentage <= 0.5) {
                          imageName = 'progress-50.png';
                        } else if (percentage <= 0.75) {
                          imageName = 'progress-75.png';
                        } else {
                          imageName = 'progress-100.png';
                        }
                        
                        return (
                          <Box sx={{ position: 'relative', width: '80px', height: '80px' }}>
                            <img 
                              src={`${process.env.PUBLIC_URL}/${imageName}`} 
                              alt={`${Math.round(percentage * 100)}% complete`}
                              style={{ width: '100%', height: '100%' }}
                            />
                          </Box>
                        );
                      })()}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
          
              {/* Right Column */}
              <Grid item xs={12} md={8}>
                {/* Problem Description - always shown for all issue types */}
                <Divider textAlign="left" sx={{  mb: 2 }}>Problem Description *</Divider>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={12}>
                    <TextField
                      name="problem"
                      label="Problem Description"
                      value={formData.problem || ''}
                      onChange={handleTextChange}
                      fullWidth
                      multiline
                      rows={6}
                      required
                    />
                  </Grid>
                </Grid>
                <Divider textAlign="left">Problem Pictures</Divider>
                {/* Problem Photos Section */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sx={{ my: 2 }}>
                    <Box>
                      <input
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png,.gif"
                        onChange={(e) => handleFileChange(e, 'problem')}
                        style={{ display: 'none' }}
                        ref={problemFileInputRef}
                      />
                      <Button
                        variant="outlined"
                        startIcon={<AddPhotoAlternateIcon />}
                        onClick={() => problemFileInputRef.current?.click()}
                        sx={{ mr: 1 }}
                        disabled={isCompressing}
                      >
                        Add Problem Photos
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CameraAltIcon />}
                        onClick={(e) => handleCameraMenuOpen(e, 'problem')}
                        disabled={isCompressing}
                      >
                        Take Problem Photo
                      </Button>
                      {isCompressing && (
                        <CircularProgress size={24} sx={{ ml: 2 }} />
                      )}
                      <Menu
                        anchorEl={cameraAnchorEl}
                        open={Boolean(cameraAnchorEl)}
                        onClose={handleCameraMenuClose}
                      >
                        <MenuItem onClick={() => handleCapturePhoto('environment', currentPhotoType)}>
                          Use Back Camera
                        </MenuItem>
                        <MenuItem onClick={() => handleCapturePhoto('user', currentPhotoType)}>
                          Use Front Camera
                        </MenuItem>
                      </Menu>
                    </Box>

                    {/* Selected Problem Files Preview */}
                    {selectedProblemFiles.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Selected Problem Photos
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                          {selectedProblemFiles.map((file, index) => (
                            <Box
                              key={index}
                              sx={{
                                position: 'relative',
                                paddingTop: '100%', // 1:1 Aspect ratio
                                borderRadius: 1,
                                overflow: 'hidden',
                                boxShadow: 1,
                                bgcolor: 'background.paper',
                              }}
                            >
                              <Box
                                component="img"
                                src={URL.createObjectURL(file)}
                                alt={`Preview ${index + 1}`}
                                onClick={() => setSelectedImage(URL.createObjectURL(file))}
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                  },
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(index, 'problem')}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                                  },
                                  zIndex: 1,
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Existing Problem Photos */}
                    {existingProblemPhotos.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Existing Problem Photos
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                          {existingProblemPhotos.map((photo) => (
                            <Box
                              key={photo.id}
                              sx={{
                                position: 'relative',
                                paddingTop: '100%',
                                borderRadius: 1,
                                overflow: 'hidden',
                                boxShadow: 1,
                                bgcolor: 'background.paper',
                              }}
                            >
                              <Box
                                component="img"
                                src={getImageUrl(photo.file_name)}
                                alt={`Issue photo ${photo.id}`}
                                onClick={() => setSelectedImage(getImageUrl(photo.file_name))}
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                  },
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleDeletePhoto(photo.id)}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                                  },
                                  zIndex: 1,
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <Divider textAlign="left" sx={{  mb: 2 }}>Counter Measures</Divider>
                {/* Counter Measures */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <TextField
                      name="counter_measure"
                      label="Counter Measure Request"
                      value={formData.counter_measure || ''}
                      onChange={handleTextChange}
                      fullWidth
                      multiline
                      rows={6}
                    />
                  </Grid>
                </Grid>
                
                {/* Final Counter Measure Section - Only shown when status is Waiting Review or Waiting Approval */}
                {(formData.status === 'waiting_review' || formData.status === 'waiting_approval') && (
                  <>
                    <Divider textAlign="left" sx={{ mt: 4, mb: 2 }}>Final Counter Measure</Divider>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12}>
                        <TextField
                          name="final_counter_measure"
                          label="Final Counter Measure"
                          value={formData.final_counter_measure || ''}
                          onChange={handleTextChange}
                          fullWidth
                          multiline
                          rows={6}
                        />
                      </Grid>
                    </Grid>
                    
                    {/* Final Counter Measure Photos Section */}
                    <Divider textAlign="left" sx={{ mt: 2, mb: 2 }}>Final Counter Measure Pictures</Divider>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sx={{ my: 2 }}>
                        <Box>
                          <input
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,.gif"
                            onChange={(e) => handleFileChange(e, 'counter_measure')}
                            style={{ display: 'none' }}
                            ref={counterMeasureFileInputRef}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<AddPhotoAlternateIcon />}
                            onClick={() => counterMeasureFileInputRef.current?.click()}
                            sx={{ mr: 1 }}
                            disabled={isCompressing}
                          >
                            Add Final Counter Measure Photos
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<CameraAltIcon />}
                            onClick={(e) => handleCameraMenuOpen(e, 'counter_measure')}
                            disabled={isCompressing}
                          >
                            Take Final Counter Measure Photo
                          </Button>
                          {isCompressing && (
                            <CircularProgress size={24} sx={{ ml: 2 }} />
                          )}
                        </Box>

                        {/* Selected Counter Measure Files Preview */}
                        {selectedCounterMeasureFiles.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Selected Final Counter Measure Photos
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                              {selectedCounterMeasureFiles.map((file, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    position: 'relative',
                                    paddingTop: '100%', // 1:1 Aspect ratio
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    boxShadow: 1,
                                    bgcolor: 'background.paper',
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={URL.createObjectURL(file)}
                                    alt={`Preview ${index + 1}`}
                                    onClick={() => setSelectedImage(URL.createObjectURL(file))}
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                      },
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveFile(index, 'counter_measure')}
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                                      },
                                      zIndex: 1,
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Existing Counter Measure Photos */}
                        {existingCounterMeasurePhotos.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Existing Final Counter Measure Photos
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                              {existingCounterMeasurePhotos.map((photo) => (
                                <Box
                                  key={photo.id}
                                  sx={{
                                    position: 'relative',
                                    paddingTop: '100%',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    boxShadow: 1,
                                    bgcolor: 'background.paper',
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={getImageUrl(photo.file_name)}
                                    alt={`Issue photo ${photo.id}`}
                                    onClick={() => setSelectedImage(getImageUrl(photo.file_name))}
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                      },
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeletePhoto(photo.id)}
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                                      },
                                      zIndex: 1,
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              {!issueId && (
                <>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={uploading}
                    onClick={(e) => handleSubmit(e, 'draft')}
                    startIcon={uploading && <CircularProgress size={20} />}
                  >
                    Create BIRA
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="success"
                    disabled={uploading}
                    onClick={(e) => handleSubmit(e, 'waiting_response')}
                    startIcon={uploading && <CircularProgress size={20} />}
                  >
                    Submit BIRA
                  </Button>
                </>
              )}
              {issueId && (
                <>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={uploading}
                    onClick={(e) => handleSubmit(e, 'draft')}
                    startIcon={uploading && <CircularProgress size={20} />}
                  >
                    Update BIRA
                  </Button>
                  {formData.status === 'draft' && (
                    <Button
                      type="button"
                      variant="contained"
                      color="success"
                      disabled={uploading}
                      onClick={(e) => handleSubmit(e, 'waiting_response')}
                      startIcon={uploading && <CircularProgress size={20} />}
                    >
                      Submit BIRA
                    </Button>
                  )}
                </>
              )}
              <Button
                variant="outlined"
                onClick={() => navigate(`/projects/${projectId}`)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>

        {/* In-app Camera Dialog */}
        <Dialog open={cameraDialogOpen} onClose={handleCameraDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            {requestedFacingMode === 'user' ? 'Front Camera' : 'Back Camera'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ position: 'relative', width: '100%', bgcolor: 'black', borderRadius: 1, overflow: 'hidden' }}>
              <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: 'auto' }} />
              {cameraLoading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress color="inherit" />
                </Box>
              )}
            </Box>
            {cameraError && (
              <Alert severity="error" sx={{ mt: 2 }}>{cameraError}</Alert>
            )}
            {/* hidden canvas for snapshot */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCameraDialogClose}>Cancel</Button>
            <Button onClick={handleTakeSnapshot} variant="contained" disabled={cameraLoading || !!cameraError}>Capture</Button>
          </DialogActions>
        </Dialog>

        {/* Photo Preview Dialog */}
        {selectedImage && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
              p: 4,
            }}
            onClick={handleClosePreview}
          >
            <Box
              sx={{
                position: 'relative',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
              <IconButton
                onClick={handleClosePreview}
                sx={{
                  position: 'absolute',
                  top: -40,
                  right: -40,
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleSnackbarClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}
